import { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Stack,
  Typography,
  Button,
  CircularProgress,
  Grid,
  Box,
  Divider,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoIcon from "@mui/icons-material/Info";
import EditIcon from "@mui/icons-material/Edit";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { getAllOrders } from "../../../services/Admin/OrdersApi";
import { getClientsAccounts } from "../../../services/Admin/AccountsAdminAndUsersApi"; // <- new import (تأكد من المسار حسب مشروعك)
import { getShippingCategories } from "../../../services/Admin/ShippingCategoriesApi"; // new: جلب تصنيفات الشحن/الدول
import { getGovernorates } from "../../../services/Admin/ShippingCategoriesApi"; // new: جلب المحافظات (تأكد من المسار في مشروعك)
import { getCities } from "../../../services/Admin/ShippingCategoriesApi"; // new: جلب المدن (تأكد من المسار في مشروعك)
import OrderDetailsDialog from "./OrderDetailsDialog";
import EditOrderStatusDialog from "./EditOrderStatusDialog";
import ClientDetailsDialog from "./ClientDetailsDialog"; // new dialog import
import ExportToExcel from "./ExportToExcel"; // new: استيراد المكوّن الجديد

const OrdersPage = () => {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState("");

  const [openOrderDialog, setOpenOrderDialog] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [openEditStatusDialog, setOpenEditStatusDialog] = useState(false);
  const [selectedOrderForStatus, setSelectedOrderForStatus] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [clientsMap, setClientsMap] = useState({}); // key: client id -> client object
  const [shippingMap, setShippingMap] = useState({}); // key: country id -> country name (e.g. name_ar)
  const [governoratesMap, setGovernoratesMap] = useState({}); // key: governorate id -> { name, shipping }
  const [citiesMap, setCitiesMap] = useState({}); // key: city id -> { name, category, governorate_name }
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // new: status filter
  const [paymentFilter, setPaymentFilter] = useState("all"); // new: payment method filter
  const [countryFilter, setCountryFilter] = useState("all");
  const [governorateFilter, setGovernorateFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortDir, setSortDir] = useState("desc"); // 'asc' or 'desc'
  const [openClientDialog, setOpenClientDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  const fetch = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await getAllOrders();
      const data = res?.success ? res.data : res;
      setOrders(Array.isArray(data) ? data : []);
      // ensure automatic default sorting newest->oldest and reset pagination
      setSortDir("desc");
      setPage(0);
    } catch (err) {
      setError(err?.message || "فشل تحميل الطلبات");
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await getClientsAccounts();
      const list = res?.success && Array.isArray(res.data) ? res.data : (Array.isArray(res) ? res : []);
      const map = {};
      list.forEach((c) => {
        if (c && (c.id != null)) map[c.id] = c; // store full client object
      });
      setClientsMap(map);
    } catch (err) {
      // ignore client-load errors (optional: set error state)
      setClientsMap({});
    }
  };

  // new: fetch shipping categories (countries) and build map id -> name_ar (fallback to name_en if needed)
  const fetchShippingCategories = async () => {
    try {
      const res = await getShippingCategories();
      const list = res?.success && Array.isArray(res.data) ? res.data : (Array.isArray(res) ? res : []);
      const map = {};
      list.forEach((item) => {
        const country = item?.country;
        if (country && (country.id != null)) {
          map[country.id] = country.name_ar || country.name_en || country.name || String(country.id);
        }
      });
      setShippingMap(map);
    } catch (err) {
      setShippingMap({});
    }
  };

  // new: fetch governorates and build map id -> { name, shipping }
  const fetchGovernorates = async () => {
    try {
      const res = await getGovernorates();
      const dataObj = res?.success ? res.data : res?.data || res || {};
      const map = {};
      // dataObj structure: { "countryId": [ { id, name_ar, shippingcategory: { name } }, ... ], ... }
      Object.keys(dataObj || {}).forEach((countryKey) => {
        const arr = Array.isArray(dataObj[countryKey]) ? dataObj[countryKey] : [];
        arr.forEach((g) => {
          if (g && (g.id != null)) {
            map[g.id] = {
              name: g.name_ar || g.name || g.name_en || String(g.id),
              shipping: g.shippingcategory?.name || null,
              country_id: Number(countryKey),
            };
          }
        });
      });
      setGovernoratesMap(map);
    } catch (err) {
      setGovernoratesMap({});
    }
  };

  // new: fetch cities and build map id -> { name, category, governorate_name }
  const fetchCities = async () => {
    try {
      const res = await getCities();
      const dataObj = res?.success ? res.data : res?.data || res || {};
      const map = {};
      // dataObj structure: { "countryId": [ { id, name_ar, category: { name }, governorate: { name_ar } }, ... ], ... }
      Object.keys(dataObj || {}).forEach((countryKey) => {
        const arr = Array.isArray(dataObj[countryKey]) ? dataObj[countryKey] : [];
        arr.forEach((c) => {
          if (c && (c.id != null)) {
            map[c.id] = {
              name: c.name_ar || c.name || c.name_en || String(c.id),
              category: c.category?.name || c.shippingcategory?.name || null,
              governorate_name: c.governorate?.name_ar || c.governorate?.name || null,
              country_id: Number(countryKey),
            };
          }
        });
      });
      setCitiesMap(map);
    } catch (err) {
      setCitiesMap({});
    }
  };

  useEffect(() => {
    fetch();
    fetchClients();
    fetchShippingCategories();
    fetchGovernorates();
    fetchCities();
  }, []);

  const formatMoney = (v) => {
    if (v == null || v === "") return "-";
    const n = Number(v);
    return Number.isNaN(n) ? v : n.toFixed(2);
  };

  // new: format created_at to 12-hour display
  const formatDate12 = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(d);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getClientDisplay = (userId) => {
    if (!userId) return "-";
    const c = clientsMap[userId];
    if (!c) return userId;
    return `${c.name || c.email || "-"} — ${c.phone || "-"}`;
  };

  // compute unique statuses for the filter dropdown
  const uniqueStatuses = (() => {
    const setS = new Set();
    orders.forEach((o) => { if (o && o.status != null) setS.add(String(o.status)); });
    return Array.from(setS);
  })();
  // compute unique payment methods for filter dropdown
  const uniquePayments = (() => {
    const setP = new Set();
    orders.forEach((o) => { if (o && o.payment_method != null) setP.add(String(o.payment_method)); });
    return Array.from(setP);
  })();

  // countries / governorates / cities derived lists for selects
  const uniqueCountries = (() => {
    return Object.keys(shippingMap).map((id) => ({ id, name: shippingMap[id] }));
  })();

  const uniqueGovernoratesFiltered = (() => {
    const all = Object.entries(governoratesMap).map(([id, g]) => ({ id, ...g }));
    if (countryFilter && countryFilter !== "all") {
      return all.filter((g) => String(g.country_id) === String(countryFilter));
    }
    return all;
  })();

  const uniqueCitiesFiltered = (() => {
    const all = Object.entries(citiesMap).map(([id, c]) => ({ id, ...c }));
    let res = all;
    if (countryFilter && countryFilter !== "all") {
      res = res.filter((c) => String(c.country_id) === String(countryFilter));
    }
    if (governorateFilter && governorateFilter !== "all") {
      res = res.filter((c) => String(c.governorate_id) === String(governorateFilter));
    }
    return res;
  })();

  const matchesQuery = (o, q) => {
    if (!q) q = searchQuery; // keep behavior if called standalone
    // country/governorate/city filters
    if (countryFilter && countryFilter !== "all") {
      if (String(o.check_out?.country_id) !== String(countryFilter)) return false;
    }
    if (governorateFilter && governorateFilter !== "all") {
      if (String(o.check_out?.governorate_id) !== String(governorateFilter)) return false;
    }
    if (cityFilter && cityFilter !== "all") {
      if (String(o.check_out?.city_id) !== String(cityFilter)) return false;
    }
    // payment filter
    if (paymentFilter && paymentFilter !== "all") {
      if (String(o.payment_method) !== String(paymentFilter)) return false;
    }
    // status filter
    if (statusFilter && statusFilter !== "all") {
      if (String(o.status) !== String(statusFilter)) return false;
    }
    if (!q && !dateFrom && !dateTo) return true;
    const s = q ? String(q).trim().toLowerCase() : null;
    const fields = [];
    fields.push(o.id, o.order_number, o.status, o.payment_method, o.total, o.created_at);
    // checkout fields
    const co = o.check_out || {};
    fields.push(co.phone, co.address, co.postal_code, co.notes, co.subtotal, co.shipping_cost, co.discount);
    // client
    const uid = o.user_id ?? o.user?.id;
    const client = uid ? clientsMap[uid] : null;
    if (client) fields.push(client.name, client.email, client.phone);
    // mapped names
    if (co.country_id) fields.push(shippingMap[co.country_id]);
    if (co.governorate_id) fields.push(governoratesMap[co.governorate_id]?.name);
    if (co.city_id) fields.push(citiesMap[co.city_id]?.name);

    // date range filter (created_at)
    if (dateFrom || dateTo) {
      const created = o.created_at ? new Date(o.created_at) : null;
      if (!created) return false;
      if (dateFrom) {
        const from = new Date(dateFrom);
        from.setHours(0, 0, 0, 0);
        if (created < from) return false;
      }
      if (dateTo) {
        const to = new Date(dateTo);
        to.setHours(23, 59, 59, 999);
        if (created > to) return false;
      }
    }

    return fields
      .filter(Boolean)
      .map((v) => String(v).toLowerCase())
      .some((v) => s ? v.includes(s) : true);
  };

  const filteredOrders = orders.filter((o) => matchesQuery(o, searchQuery));

  // sorted according to created_at and sortDir
  const sortedOrders = filteredOrders.slice().sort((a, b) => {
    const da = a?.created_at ? new Date(a.created_at).getTime() : 0;
    const db = b?.created_at ? new Date(b.created_at).getTime() : 0;
    if (!da && !db) return 0;
    if (sortDir === "asc") return da - db;
    return db - da;
  });

  const toggleSortDir = () => {
    setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    setPage(0);
  };

  return (
    <Container maxWidth={false} sx={{ py: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, gap: 1 }}>
          <Box>
            <Typography variant="h6">الطلبات</Typography>
            <Typography variant="body2" color="text.secondary">عرض جميع الطلبات</Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
              <Select
                size="small"
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="all">كل الحالات</MenuItem>
                {Object.keys(STATUS_MAP).map((key) => (
                  <MenuItem key={key} value={key}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, bgcolor: STATUS_MAP[key].bg, borderRadius: 0 }} />
                      <Typography variant="body2">{STATUS_MAP[key].label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              <Select
                size="small"
                value={countryFilter}
                onChange={(e) => { setCountryFilter(e.target.value); setGovernorateFilter("all"); setCityFilter("all"); setPage(0); }}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="all">كل الدول</MenuItem>
                {uniqueCountries.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
              <Select
                size="small"
                value={governorateFilter}
                onChange={(e) => { setGovernorateFilter(e.target.value); setCityFilter("all"); setPage(0); }}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="all">كل المحافظات</MenuItem>
                {uniqueGovernoratesFiltered.map((g) => (
                  <MenuItem key={g.id} value={g.id}>{g.name}{g.shipping ? ` — ${g.shipping}` : ""}</MenuItem>
                ))}
              </Select>
              <Select
                size="small"
                value={cityFilter}
                onChange={(e) => { setCityFilter(e.target.value); setPage(0); }}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="all">كل المدن</MenuItem>
                {uniqueCitiesFiltered.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}{c.category ? ` — ${c.category}` : ""}</MenuItem>
                ))}
              </Select>
              <Select
                size="small"
                value={paymentFilter}
                onChange={(e) => { setPaymentFilter(e.target.value); setPage(0); }}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="all">كل طرق الدفع</MenuItem>
                {uniquePayments.map((pm) => (
                  <MenuItem key={pm} value={pm}>{pm}</MenuItem>
                ))}
              </Select>
              <TextField
                size="small"
                type="date"
                label="من"
                InputLabelProps={{ shrink: true }}
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
                sx={{ minWidth: 140 }}
              />
              <TextField
                size="small"
                type="date"
                label="إلى"
                InputLabelProps={{ shrink: true }}
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
                sx={{ minWidth: 140 }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
              <TextField
                size="small"
                placeholder="ابحث في الطلبات..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                sx={{ minWidth: 240, flex: 1 }}
              />
              <Button startIcon={<RefreshIcon />} onClick={fetch} disabled={loading} variant="outlined" size="small">
                {loading ? "جارٍ..." : "تحديث"}
              </Button>
              {/* Export button: exports the currently displayed data (filtered+sorted) */}
              <ExportToExcel
                data={sortedOrders}
                clientsMap={clientsMap}
                shippingMap={shippingMap}
                governoratesMap={governoratesMap}
                citiesMap={citiesMap}
                filename="orders_export.csv"
              />
            </Box>
          </Box>
        </Stack>

        {loading ? (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : !orders.length ? (
          <Typography color="text.secondary">لا توجد طلبات</Typography>
        ) : (
          <>
            <TableContainer component={Paper} sx={{ direction: "rtl" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="right">رقم الطلب</TableCell>
                    <TableCell align="right">العميل</TableCell>
                    <TableCell align="right">هاتف الطلب</TableCell>
                    <TableCell align="right">الحالة</TableCell>
                    <TableCell align="right">طريقة الدفع</TableCell>
                    <TableCell align="right">الإجمالي</TableCell>
                    <TableCell align="right">الشحن</TableCell>
                    <TableCell align="right">الخصم</TableCell>
                    <TableCell align="right">الاجمالي الكلي</TableCell>
                    <TableCell align="right">البلد</TableCell>
                    <TableCell align="right">المحافظة</TableCell>
                    <TableCell align="right">المدينة</TableCell>
                    <TableCell align="right">العنوان</TableCell>
                    <TableCell align="right">الرمز البريدي</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <Button size="small" onClick={toggleSortDir} endIcon={sortDir === "asc" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />} sx={{ textTransform: "none" }}>
                        تاريخ الإنشاء
                      </Button>
                    </TableCell>
                    <TableCell align="center">إجراءات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((o) => (
                    <TableRow key={o.id} hover>
                      <TableCell align="right">{o.order_number}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="text"
                          onClick={(e) => {
                            e.stopPropagation();
                            const uid = o.user_id ?? o.user?.id;
                            const client = clientsMap[uid] || (uid ? { id: uid } : null);
                            setSelectedClient(client);
                            setOpenClientDialog(true);
                          }}
                        >
                          {getClientDisplay(o.user_id ?? o.user?.id)}
                        </Button>
                      </TableCell>
                      <TableCell align="right">
                        {o.check_out?.phone || clientsMap[o.user_id ?? o.user?.id]?.phone || "-"}
                      </TableCell>
                      <TableCell align="right">
                        {(() => {
                          const s = String(o.status || "").toLowerCase();
                          const info = STATUS_MAP[s];
                          return info ? (
                            <Chip
                              label={info.label}
                              size="small"
                              sx={{ backgroundColor: info.bg, color: info.text, fontWeight: 600 }}
                            />
                          ) : (
                            <Chip label={o.status} size="small" />
                          );
                        })()}
                      </TableCell>
                      <TableCell align="right">{o.payment_method || "-"}</TableCell>
                      <TableCell align="right">{formatMoney(o.total)}</TableCell>
                      <TableCell align="right">{formatMoney(o.check_out?.shipping_cost || o.check_out?.shipping)}</TableCell>
                      <TableCell align="right">{formatMoney(o.check_out?.discount)}</TableCell>
                      <TableCell align="right">{formatMoney(o.check_out?.subtotal)}</TableCell>
                      <TableCell align="right">
                        {shippingMap[o.check_out?.country_id] ?? o.check_out?.country_id ?? "-"}
                      </TableCell>
                      <TableCell align="right" sx={{ maxWidth: 220 }}>
                        {(() => {
                          const gid = o.check_out?.governorate_id;
                          const g = gid ? governoratesMap[gid] : null;
                          return g ? (
                            <>
                              <div style={{ fontWeight: 600 }}>{g.name}</div>
                              {g.shipping ? <div style={{ fontSize: 12, color: "rgba(0,0,0,0.6)" }}>{g.shipping}</div> : null}
                            </>
                          ) : (gid ?? "-");
                        })()}
                      </TableCell>
                      <TableCell align="right" sx={{ maxWidth: 200 }}>
                        {(() => {
                          const cid = o.check_out?.city_id;
                          const c = cid ? citiesMap[cid] : null;
                          return c ? (
                            <>
                              <div style={{ fontWeight: 600 }}>{c.name}</div>
                              {c.category ? <div style={{ fontSize: 12, color: "rgba(0,0,0,0.6)" }}>{c.category}</div> : null}
                            </>
                          ) : (cid ?? "-");
                        })()}
                      </TableCell>
                      <TableCell align="right">{o.check_out?.address || "-"}</TableCell>
                      <TableCell align="right">{o.check_out?.postal_code || "-"}</TableCell>
                      <TableCell align="right" sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {o.check_out?.notes || "-"} 
                      </TableCell>
                      <TableCell align="right">{formatDate12(o.created_at)}</TableCell>

                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedOrderId(o.id);
                            setOpenOrderDialog(true);
                          }}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            // build enriched order object with client + checkout readable names + country
                            const uid = o.user_id ?? o.user?.id;
                            const client = clientsMap[uid] || null;
                            const co = o.check_out || {};
                            const cityName = co?.city_id ? (citiesMap[co.city_id]?.name || null) : null;
                            const governorateName = co?.governorate_id ? (governoratesMap[co.governorate_id]?.name || null) : null;
                            const countryName = co?.country_id ? (shippingMap[co.country_id] || co.country_id) : null;
                            const enriched = {
                              ...o,
                              client,
                              checkout: co,
                              address: co?.address || null,
                              phone: co?.phone || null,
                              city: cityName,
                              governorate: governorateName,
                              country: countryName,
                              order_number: o.order_number,
                            };
                            setSelectedOrderForStatus(enriched);
                            setOpenEditStatusDialog(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredOrders.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </>
        )}
      </Paper>

      <OrderDetailsDialog
        open={openOrderDialog}
        onClose={() => {
          setOpenOrderDialog(false);
          setSelectedOrderId(null);
        }}
        orderId={selectedOrderId}
      />

      <EditOrderStatusDialog
        open={openEditStatusDialog}
        onClose={() => {
          setOpenEditStatusDialog(false);
          setSelectedOrderForStatus(null);
        }}
        order={selectedOrderForStatus}
        onSuccess={() => {
          setOpenEditStatusDialog(false);
          setSelectedOrderForStatus(null);
          fetch();
        }}
      />

      {/* client details dialog */}
      <ClientDetailsDialog
        open={openClientDialog}
        client={selectedClient}
        onClose={() => {
          setOpenClientDialog(false);
          setSelectedClient(null);
        }}
      />
    </Container>
  );
};

export default OrdersPage;

// map status -> { labelAr, bg, text }
const STATUS_MAP = {
  pending: { label: "قيد الانتظار", bg: "#FFC107", text: "#000" }, // Amber
  processing: { label: "قيد المعالجة", bg: "#1976d2", text: "#fff" }, // Blue
  confirmed: { label: "مؤكّد", bg: "#0288d1", text: "#fff" }, // Light Blue
  shipped: { label: "تم الشحن", bg: "#7E57C2", text: "#fff" }, // Purple
  delivered: { label: "تم التوصيل", bg: "#2E7D32", text: "#fff" }, // Green
  canceled: { label: "ملغى", bg: "#D32F2F", text: "#fff" }, // Red
};
